import axios, { Method, AxiosRequestConfig, AxiosInstance } from 'axios';
import {
  Response,
  TransactionHisReq,
  TransactionHisRes, // SportyBet
  AccountInfoRes,
  ListCategroryReq,
  ListCategroryRes, // 1.1 List categories
  ShowCategoryReq,
  ShowCategoryRes, // 1.2 Show category
  CategorySectionsReq,
  CategorySectionsRes, // 2.1 Get category sections
  SubsectionsReq,
  SubsectionsRes, // 2.2 Get subsections
  ShowSectionReq,
  ShowSectionRes, // 2.3 Show section
  SectionArticleTreeReq,
  SectionArticleTreeRes, // 2.4 Get Section Article Tree
  SectionArticleReq,
  SectionArticleRes, // 3.1 List articles for a section
  ShowArticleReq,
  ShowArticleRes, // 3.2 Show article
  ArticleCommentsReq,
  ArticleCommentsRes, // 3.3 Get article comments
  ArticleAttachmentReq,
  ArticleAttachmentRes, // 3.4 Get article attachments
  ArticlePopularReq,
  ArticlePopularRes, // 3.9 Get popular articles
  ArticlePopularTreeRes,
  ArticlePopularTreeReq, // 3.10 Get related articles
  // 4.1 Create Anonymous User
  // 4.2 Get User Details
  UserDetailsRes,
  TicketFieldsResponse, // 1. Get ticket fields
  CreateLoggedInTicketReqBody,
  CreateLoggedInTicketRes, // 2. Create ticket (logged in user)
  CreateVisitorTicketReqBody,
  CreateVisitorTicketRes, // 3. Create ticket (non logged in user)
  UserTciketReq,
  UserTciketRes, // 4. List user tickets (Login mandatory)
  ShowTicketReq,
  ShowTicketRes, // 5. Show ticket (login mandatory)
  TicketListCommentsReq,
  TicketListCommentsRes, // 6. List ticket comments (login mandatory)
  CreateTicketCommentReqBody, // 7. add comment (login mandatory)
  UploadAttachmentRes,
  SearchTicketReq, // 8. Search ticket (login mandatory)
  CreateTicketCommentRes,
  RuleEngineReq,
  RuleEngineRes, // 5.1 Rule Engine to ask Issue Resolved Question
  // 6.1 . Fetch the current queue position on the live chat
  // 6.2 Assign chat ticket to user
  TicketToUserReqBody,
  BrandTokenRes,
  RefreshTransactionStatusReqBody,
  RefreshTransactionStatusRes,
  PayChannelSuccessRate,
  // 8.1 Inactivate user activity after endchat
} from './model';

import URL from './url';
import { Country, CustomerServiceProvider } from '../config';
import { getBridge } from '@/common/bridge/getBridge';
import { attachAxiosErrorTracking, captureApiFailure, isCancelledRequest } from '@/common/error-tracking';
import { parseErrorMessage } from '@/common/error';

let CountryCode: Country = Country.NG; // TODO
let SFToken = '';
let SUPPORT_JWE_TOKEN = false;
class Api {
  axios: AxiosInstance;
  sbAxios: AxiosInstance;
  constructor() {
    this.axios = axios.create({});
    this.sbAxios = axios.create({});
    // Network failures on Sporty/Zendesk clients are often swallowed by callers;
    // report here so they still reach GlitchTip (status/URL only).
    attachAxiosErrorTracking(this.axios);
    attachAxiosErrorTracking(this.sbAxios);
  }

  private request<T>(method: Method, config: AxiosRequestConfig): Promise<T> {
    return this.axios
      .request({
        method,
        ...config,
      })
      .then((res) => res.data)
      .catch((err) => {
        throw err;
      });
  }
  // TODO: [Sportybet]
  private sbRequest<T>(method: Method, config: AxiosRequestConfig): Promise<T> {
    // WAP/WEB new flow: delegate SB API calls to host page (no access token in FE).
    if (SUPPORT_JWE_TOKEN) {
      const baseUrl = config.url?.replace(`/api/${CountryCode}/`, '') || '';
      const params = config.params || {};
      const query = new URLSearchParams(
        Object.entries(params as Record<string, string>).filter(([, v]) => v !== undefined && v !== null && v !== '')
      ).toString();
      const url = query ? `${baseUrl}?${query}` : baseUrl;
      const upperMethod = `${method || 'GET'}`.toUpperCase();
      const rawHeaders = (config.headers || {}) as Record<string, string>;
      const headers = Object.fromEntries(
        Object.entries(rawHeaders).filter(([, v]) => v !== undefined && v !== null && v !== '')
      );
      const json = config.data;
      return getBridge()
        .callParentApi({ url, method: upperMethod, json: json, headers })
        .then((result) => {
          return result as T;
        })
        .catch((err) => {
          // Bridge path bypasses axios interceptors — report status/URL-equivalent path here.
          // The host only hands back an error text, so it carries the whole diagnosis.
          if (!isCancelledRequest(err)) {
            captureApiFailure({
              event: 'API Network Error',
              url,
              method: upperMethod,
              reason: parseErrorMessage(err),
            });
          }
          throw err;
        });
    }

    return this.sbAxios
      .request({
        method,
        ...config,
      })
      .then((res) => res.data)
      .catch((err) => {
        throw err;
      });
  }
  setCountry(country: Country) {
    this.axios.defaults.headers.common['country-code'] = country;
    this.sbAxios.defaults.headers.common['country-code'] = country;
    CountryCode = country;
  }

  setProvider(provider: CustomerServiceProvider) {
    const url = provider === CustomerServiceProvider.PLEASED ? '/api/v1/sportydesk-pleased' : '/api/v1/sportydesk';
    this.axios.defaults.baseURL = url;
  }

  setAnonymousToken(anonymousToken: string) {
    this.axios.defaults.headers.common['anonymous-user-access-token'] = anonymousToken;
  }
  setTokenToHeader(sportyToken: string) {
    this.axios.defaults.headers.common['sf-access-token'] = sportyToken;
    SFToken = sportyToken;
  }

  setSupportJweToken(enabled: boolean) {
    SUPPORT_JWE_TOKEN = !!enabled;
  }

  // TODO: [Sportybet]
  getTransactionHistory(params: TransactionHisReq) {
    return this.sbRequest<Response<TransactionHisRes>>('GET', {
      url: `/api/${CountryCode}/pocket/v1/statements`,
      // localhost:8080/api... net work
      // www.test.easebt.com/api/.... proxy sportydesk -> localhost -> www.test.easebet.com
      // host -> www.test.easebet.com 127.0.0.1
      // ftl-node 127.0.0.1 -> fe/build
      // if it cannot find in fe/build path, then go to the network
      params,
      headers: {
        Authorization: SFToken,
      },
    });
  }
  // TODO: [Sportybet]
  getBetHistory(params: any) {
    return this.sbRequest<Response<any>>('GET', {
      url: `/api/${CountryCode}/orders/order/v2/realbetlist`,
      params,
      headers: {
        Authorization: SFToken,
      },
    });
  }
  // TODO: [Sportybet]
  getAccountInfo(params?: { _t: string }) {
    return this.sbRequest<Response<AccountInfoRes>>('GET', {
      url: `/api/${CountryCode}/patron/account/info`,
      params,
      headers: {
        Authorization: SFToken,
      },
    });
  }

  getAlertInfo(type: 'deposit' | 'withdraw') {
    return this.sbRequest<Response<PayChannelSuccessRate[]>>('GET', {
      url: `/api/${CountryCode}/pocket/v1/pocket/dropAlert/display/${type}`,
      headers: {
        Authorization: SFToken,
      },
    });
  }
  // TODO: [Sportybet]
  checkEmailValid(params?: { _t: string }) {
    return this.sbRequest<Response<boolean>>('GET', {
      url: `/api/${CountryCode}/patron/mail/verify/check`,
      params,
      headers: {
        Authorization: SFToken,
      },
    });
  }

  refreshTransactionStatus(data: RefreshTransactionStatusReqBody) {
    return this.sbRequest<Response<RefreshTransactionStatusRes>>('POST', {
      url: `/api/${CountryCode}/pocket/v1/paych/front/manualClaim`,
      data,
      headers: {
        Authorization: SFToken,
      },
    });
  }

  // SportyDesk
  // 1.1 List categories
  getListCategory(params?: ListCategroryReq) {
    return this.request<Response<ListCategroryRes>>('GET', {
      url: URL.LIST_CATEGORY,
      params,
      // headers: {
      //   'anonymous-user-access-token': anonymousToken
      // }
    });
  }
  // 1.2 Show category (not used)
  getShowCategory(params: ShowCategoryReq) {
    return this.request<Response<ShowCategoryRes>>('GET', {
      url: URL.SHOW_CATEGORY,
      params,
    });
  }
  // 2.1 Get category sections
  getCategorySections(params: CategorySectionsReq) {
    return this.request<Response<CategorySectionsRes>>('GET', {
      url: URL.CATEGORY_SECTIONS,
      params,
      // headers: {
      //   'anonymous-user-access-token': anonymousToken
      // }
    });
  }
  // 2.2 Get subsections
  getSubsection(params: SubsectionsReq) {
    return this.request<Response<SubsectionsRes>>('GET', {
      url: URL.SUBSECTIONS,
      params,
    });
  }
  // 2.3 Show section (not used)
  getShowSection(params: ShowSectionReq) {
    return this.request<Response<ShowSectionRes>>('GET', {
      url: URL.SHOW_SECTION,
      params,
    });
  }
  // 2.4 Get Section Article Tree
  getSectionArticleTree(params: SectionArticleTreeReq) {
    return this.request<Response<SectionArticleTreeRes>>('GET', {
      url: URL.SECTION_TREE,
      params,
      // headers: {
      //   'anonymous-user-access-token': anonymousToken
      // }
    });
  }
  // 3.1 List articles for a section
  getSectionArticle(params: SectionArticleReq) {
    return this.request<Response<SectionArticleRes>>('GET', {
      url: URL.SECTION_ARTICLE,
      params,
    });
  }
  // 3.2 Show article
  getShowArticle(params: ShowArticleReq) {
    return this.request<Response<ShowArticleRes>>('GET', {
      url: URL.SHOW_ARTICLE,
      params,
      // headers: {
      //   'anonymous-user-access-token': anonymousToken
      // }
    });
  }
  // 3.3 Get article comments
  getArticleComments(params: ArticleCommentsReq) {
    return this.request<Response<ArticleCommentsRes>>('GET', {
      url: URL.ARTICLE_COMMONENTS,
      params,
    });
  }
  // 3.4 Get article attachments
  getArticleAttachments(params?: ArticleAttachmentReq) {
    return this.request<Response<ArticleAttachmentRes>>('GET', {
      url: URL.ARTICLE_ATTACHMENTS,
      params,
    });
  }
  // 3.9 Get article attachments
  getPopularArticle(params?: ArticlePopularReq) {
    return this.request<Response<ArticlePopularRes>>('GET', {
      url: URL.ARTICLE_POPULAR,
      params,
      // headers: {
      //   'anonymous-user-access-token': anonymousToken
      // }
    });
  }
  // 3.10 Get related articles
  getPouplarArticleTree(params: ArticlePopularTreeReq) {
    return this.request<Response<ArticlePopularTreeRes>>('GET', {
      url: URL.ARTICLE_POPULAR_TREE,
      params,
    });
  }

  // 4.1 Create Anonymous User
  createAnnoymousUser() {
    return this.request<Response<string>>('POST', {
      url: URL.CREATE_ANNONYMOUS_USER,
    });
  }
  // 4.2 Get User Details
  getUserDetails() {
    return this.request<Response<UserDetailsRes>>('GET', {
      url: URL.USER_DETAILS,
    });
  }
  // 1. Get ticket fields
  getTicketFields() {
    return this.request<Response<TicketFieldsResponse>>('GET', {
      url: URL.TICKET_FIELDS,
    });
  }
  // 2. Create ticket (logged in user)
  createLoggedInTicket(data: CreateLoggedInTicketReqBody) {
    return this.request<Response<CreateLoggedInTicketRes>>('POST', {
      url: URL.TICKET_CREATE_LOGINED,
      data,
    });
  }
  // 3. Create ticket (non logged in user)
  createNonLoggedInTicket(data: CreateVisitorTicketReqBody) {
    return this.request<Response<CreateVisitorTicketRes>>('POST', {
      url: URL.TICKET_CREATE_NOT_LOGINED,
      // headers: {
      //   'anonymous-user-access-token': 'eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiIyMDAyMTMwMTI1MDdzdWQwMDAxMjAyOCIsImV4cCI6MTU4MTU2MDcwNywiaWF0IjoxNTgxNTU3MTA3fQ.48M4H1dkSxUH8WCfCYeBTsrR_4Wz9eVUFUHQ2cBrGYDIzhfAytsbMfyqujmfOdnC6bEvKEp3OCQsjDRfJTn0ew'
      // },
      data,
    });
  }
  // 4. List user tickets (Login mandatory)
  listUserTickets(params: UserTciketReq) {
    return this.request<Response<UserTciketRes>>('GET', {
      url: URL.USER_TICKETS,
      params,
    });
  }
  // 5. Show ticket (login mandatory)
  showTicket(params: ShowTicketReq) {
    return this.request<Response<ShowTicketRes>>('GET', {
      url: URL.SHOW_TICKETS,
      params,
    });
  }
  // 6. List ticket comments (login mandatory)
  listTicketComments(params: TicketListCommentsReq) {
    return this.request<Response<TicketListCommentsRes>>('GET', {
      url: URL.TICKET_COMMENTS,
      params,
    });
  }
  // 7. Add comment to ticket
  addComment(data: any, params: any) {
    return this.request<Response<CreateTicketCommentRes>>('POST', {
      url: URL.ADD_COMMENT,
      data,
      params,
    });
  }
  // 8. Search ticket by id or title
  searchTicket(params: SearchTicketReq) {
    return this.request<Response<UserTciketRes>>('POST', {
      url: URL.SEARCH_TICKETS,
      params,
    });
  }
  // 8. Upload attachment image for comment
  uploadAttachment(data: FormData) {
    return this.request<Response<UploadAttachmentRes>>('POST', {
      url: URL.UPLOAD_ATTACHMENT,
      headers: { 'Content-Type': undefined },
      data,
    });
  }
  // 5.1 Rule Engine to ask Issue Resolved Question
  getRuleEngine(params: RuleEngineReq) {
    return this.request<Response<RuleEngineRes>>('GET', {
      url: URL.ISSUE_RESOLVED,
      params,
    });
  }
  // 6.1 . Fetch the current queue position on the live chat
  getQueuePosition() {
    return this.request<Response<number>>('GET', {
      url: URL.QUEUE_POSITION,
    });
  }
  // 6.2 Assign chat ticket to user
  assignChatTicketToUser(data: TicketToUserReqBody) {
    return this.request<Response<null>>('PUT', {
      url: URL.ASSIGN_TICKET_USER,
      data,
    });
  }
  // 8.1 Inactivate user activity after endchat
  endChat() {
    return this.request<Response<null>>('GET', {
      url: URL.END_CHAT,
    });
  }

  // Get access token
  getAccessToken() {
    return this.request<Response<BrandTokenRes>>('POST', {
      url: URL.FETCH_TOKEN,
    });
  }
}

export default new Api();
